import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Download, Calendar, Printer } from "lucide-react";

export default function AdminFinances() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    api.get("/admin/finances/monthly", { params: { months } }).then((r) => setData(r.data));
  }, [months]);

  const series = data?.series || [];
  const max = Math.max(1, ...series.map((s) => Math.max(s.revenue, s.invoiced)));
  const totalRev = series.reduce((acc, s) => acc + s.revenue, 0);
  const totalInv = series.reduce((acc, s) => acc + s.invoiced, 0);
  const collectionRate = totalInv > 0 ? Math.round((totalRev / totalInv) * 100) : 0;

  const downloadCsv = async () => {
    const r = await api.get("/admin/invoices.csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `powerbuilt_invoices_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadXlsx = async () => {
    const r = await api.get("/admin/finances/export.xlsx", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = url; a.download = `powerbuilt_finance_${new Date().toISOString().slice(0,10)}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-finances" className="printable-root">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 print:hidden">
        <div>
          <p className="overline">Money flow</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Finances</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">Revenue + invoices over time. Click invoices to drill in.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))} data-testid="months-select"
                  className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
            <option value="3">Last 3 months</option>
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
            <option value="24">Last 24 months</option>
          </select>
          <button onClick={() => window.print()} className="btn-outline-navy text-sm py-2 px-3" data-testid="print-finances">
            <Printer className="w-4 h-4 mr-1" /> Print / PDF
          </button>
          <button onClick={downloadXlsx} className="btn-accent text-sm py-2 px-3" data-testid="export-xlsx">
            <Download className="w-4 h-4 mr-1" /> Excel (full)
          </button>
          <button onClick={downloadCsv} className="btn-outline-navy text-sm py-2 px-3" data-testid="export-invoices-csv">
            <Download className="w-4 h-4 mr-1" /> Invoices CSV
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <img src="/logo.png" alt="" className="w-12 h-12 object-contain" />
          <div>
            <div className="font-bold text-lg">POWERBUILT SXM</div>
            <div className="text-xs text-slate-500">Solar · Electrical · Maintenance</div>
          </div>
        </div>
        <h1 className="text-2xl font-bold mt-3">Financial Summary — last {months} months</h1>
        <div className="text-xs text-slate-500">Generated {new Date().toLocaleString()}</div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <KpiTile label="Revenue (period)" value={`$${totalRev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={TrendingUp} />
        <KpiTile label="Invoiced (period)" value={`$${totalInv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={Calendar} />
        <KpiTile label="Collection rate" value={`${collectionRate}%`} icon={TrendingUp} />
      </div>

      <div className="card-pb p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Monthly revenue</h2>
          <div className="flex gap-3 text-xs text-[#475569] dark:text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#C9A356]"></span> Revenue</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#0A2540] dark:bg-slate-600"></span> Invoiced</span>
          </div>
        </div>
        {series.length === 0 ? (
          <div className="py-12 text-center text-[#475569]">Loading…</div>
        ) : (
          <div className="overflow-x-auto" data-testid="finance-chart">
            <div className="flex items-end gap-3 h-64 min-w-[600px] px-2">
              {series.map((s) => {
                const revH = (s.revenue / max) * 100;
                const invH = (s.invoiced / max) * 100;
                return (
                  <div key={s.month} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="w-full flex items-end gap-1 h-full">
                      <div title={`Invoiced: $${s.invoiced.toFixed(2)}`}
                           data-testid={`bar-invoiced-${s.month}`}
                           className="flex-1 bg-[#0A2540] dark:bg-slate-600 rounded-t-sm hover:opacity-80 transition" style={{ height: `${invH}%`, minHeight: invH > 0 ? "4px" : "0" }}></div>
                      <div title={`Revenue: $${s.revenue.toFixed(2)}`}
                           data-testid={`bar-revenue-${s.month}`}
                           className="flex-1 bg-[#C9A356] rounded-t-sm hover:opacity-80 transition" style={{ height: `${revH}%`, minHeight: revH > 0 ? "4px" : "0" }}></div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 mt-2">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Monthly table */}
      <div className="card-pb overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Month</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Invoiced</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Revenue</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Tx count</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Collected %</th>
            </tr>
          </thead>
          <tbody>
            {[...series].reverse().map((s) => {
              const rate = s.invoiced > 0 ? Math.round((s.revenue / s.invoiced) * 100) : 0;
              return (
                <tr key={s.month} className="border-b border-[#E2E8F0] dark:border-slate-700" data-testid={`finance-row-${s.month}`}>
                  <td className="px-5 py-3 text-sm font-medium text-[#0A2540] dark:text-white">{s.label}</td>
                  <td className="px-5 py-3 text-right text-sm">${s.invoiced.toFixed(2)}</td>
                  <td className="px-5 py-3 text-right text-sm font-medium text-[#C9A356]">${s.revenue.toFixed(2)}</td>
                  <td className="px-5 py-3 text-right text-sm text-[#475569] dark:text-slate-400">{s.transactions}</td>
                  <td className="px-5 py-3 text-right text-sm">{rate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon }) {
  return (
    <div className="card-pb p-5">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#C9A356]" />
        <span className="overline">{label}</span>
      </div>
      <div className="font-heading text-3xl font-bold text-[#0A2540] dark:text-white tabular-nums">{value}</div>
    </div>
  );
}
